/**
 * NodePage needs to handle several entries into the page as location change can come from 3 cases
 *  * url path
 *  * changing view (i.e. alarm -> asset or similar)
 *  * changing tree root
 *  * selecting a new item in tree
 *
 */

import React, { useCallback, useState, useMemo, useReducer, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import useLocalStorageState from 'use-local-storage-state';

import { createPathUrl, parseLocation, createTreePathList, getTreeRootId } from './location';

import PropTypes from 'prop-types';
import NodeTreePage from './NodeTreePage';
import NodeContext from '../../services/Context/NodeContext';
import _ from 'lodash';
import HelpContext from '../../services/Context/HelpContext';


const REASON = Object.freeze({
  itemSelect: 'itemSelect',
  rootChange: 'rootChange'
});


const NodePage = ({ children }) => {
  const location = useLocation();
  const history = useHistory();

  const reducer = (state, { location, localStorage }) => {
    const { basePath, treeRootId, resourceIds } = parseLocation(location?.pathname);
    const currentTreeRoot = treeRootId ?? (localStorage?.lastTreeRoot ?? null);
    const currentPath = resourceIds?.treePath ?? (localStorage?.lastPath ?? []);
    switch (location.state?.reason) {
      case REASON.itemSelect:
        return state;

      case REASON.rootChange:
        let lastPath = localStorage?.[treeRootId] ?? [];
        if (_.last(_.first(lastPath)?.split('/')) !== treeRootId) { // make sure the cache is correct
          lastPath = [];
        }
        return {
          basePath: (_.isEqual(state.basePath, basePath)) ? state.basePath : basePath,
          currentTreeRoot: treeRootId,
          currentPath: lastPath
        };
      default:
        return {
          basePath: (_.isEqual(state.basePath, basePath)) ? state.basePath : basePath,
          currentTreeRoot: (_.isEqual(state.currentTreeRoot, currentTreeRoot)) ? state.currentTreeRoot : currentTreeRoot,
          currentPath: (_.isEqual(state.currentPath, currentPath)) ? state.currentPath : currentPath
        };
    }
  };


  const [localStorage, setLocalStorage] = useLocalStorageState('SelectedNodes', {
    defaultValue: ''
  });

  const reducerDefault = useMemo(() => {
    const { basePath, treeRootId, resourceIds } = parseLocation(location.pathname);
    return {
      basePath,
      treeRootId,
      resourceIds: resourceIds?.treePath,
      currentTreeRoot: treeRootId ?? (localStorage?.lastTreeRoot ?? null),
      currentPath: resourceIds?.treePath ?? (localStorage?.lastPath ?? [])
    };
  }, [localStorage?.lastPath, localStorage?.lastTreeRoot, location.pathname]);
  const [{ basePath, currentTreeRoot, currentPath }, dispatch] = useReducer(reducer, reducerDefault);
  const [selectedItem, setSelectedItem] = useState(null);

  const nodeData = useMemo(() => ({
    node: selectedItem,
    baseUrl: basePath,
    lastPath: localStorage?.lastPath ?? [],
    lastTreeRoot: localStorage?.lastTreeRoot ?? null
  }), [basePath, selectedItem, localStorage?.lastPath, localStorage?.lastTreeRoot]);


  useEffect(() => {
    dispatch({ location, localStorage });
  }, [localStorage, location]);

  const onItemSelected = useCallback(item => {
    const path = basePath ?? history.location.pathname.match(/^(\/[0-9a-zA-Z]+)/g)[0]; // basePath undefined on reload of page
    const url = `${createPathUrl(item, path)}${history.location.search}`;
    history.push({
      pathname: url,
      state: {
        reason: REASON.itemSelect
      } });
    setSelectedItem(item);
    setLocalStorage(oldSelected => {
      const path = createTreePathList(item);
      const rootId = getTreeRootId(item);
      return {
        ...oldSelected,
        [rootId]: path,
        lastPath: path,
        lastTreeRoot: rootId
      };
    });
  }, [basePath, history, setLocalStorage]);

  const _setCurrentTreeRoot = useCallback((treeRoot) => {
    history.push(
      {
        pathname: `${basePath}/tree/${treeRoot}`,
        state: { reason: REASON.rootChange }
      }
    );
  }, [basePath, history]);

  const [helpFile, setHelpFile] = useState(null);
  const help = useMemo(() => {
    return {
      helpFile,
      setHelpFile
    };
  }, [helpFile, setHelpFile]);

  return (
    <NodeContext.Provider value={nodeData}>
      <HelpContext.Provider value={help} >
        <NodeTreePage
          initialExpandedPath={currentPath}
          initialRoot={currentTreeRoot}
          onItemSelected={onItemSelected}
          onTreeRootChange={_setCurrentTreeRoot}
          help={helpFile}
        >
          { children }
        </NodeTreePage>
      </HelpContext.Provider>
    </NodeContext.Provider>
  );
};

NodePage.propTypes = {
  children: PropTypes.node
};

export default NodePage;
